import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { Dimensions, Platform, useWindowDimensions, View } from 'react-native';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';
import { useAppSelector } from '../../redux/helper';
import BackOfficeHeaderLocationPicker from '../../components/Reusables/headers/BackOfficeHeaderLocationPicker';
import { useAppDispatch } from '../../redux/helper';
import { SettingsDetails } from '../../redux/SettingsSlice';
import { fs, s, v } from '../../utils/libs';
import {
  BackOfficeOrdersFilterProvider,
  useBackOfficeOrdersFilter,
} from '../../Context/BackOfficeOrdersFilterContext';
import Filter from '../../components/Reusables/Filter';
import BackOfficeCompletedOrders from '../../screens/backoffice/orders/BackOfficeCompletedOrders';
import BackOfficePendingOrders from '../../screens/backoffice/orders/BackOfficePendingOrders';
import BackOfficePartialOrders from '../../screens/backoffice/orders/BackOfficePartialOrders';
// On web, this import resolves to `AppSearchBar.web.tsx` via resolver extensions.
import AppSearchBar from '../../components/Reusables/AppSearchBar';
import AppSearchBarWeb from '../../components/Reusables/AppSearchBar.web';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import HeaderActions from '../../components/Reusables/headers/HeaderActions';
import { isEmployeeAdminRole } from '../../utils/roles';
import { shouldShowTabletSidebarLayoutSetting } from '../../utils/layoutUtils';
import { isTablet } from 'react-native-device-info';
import {
  clearTableSearch,
  setOrdersError,
  setOrdersLoading,
  setTableSearchPagination,
  setTableSearchResults,
  setTableSearchTerm,
} from '../../redux/backoffice/ordersSlice';
import { searchOrderByReceiptId } from '../../api/backofficeOrders';

const Tab = createMaterialTopTabNavigator();

const InnerTabs: React.FC = () => {
  const { theme } = useTheme();
  const route = useRoute<any>();
  const settings = useAppSelector(SettingsDetails);
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const { width, height } = useWindowDimensions();
  const isTabletCompact = width >= 768 && width < 1024;
  const webHeaderSearchMaxWidth = isTabletCompact ? s(660) : s(620);
  const nativeHeaderSearchWidth = isTablet()
    ? Math.max(width * 0.7, width - s(110))
    : width * 0.7;
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const { useSidebarNav: useSidebar } = useLayoutSize();
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    width,
    height,
  );
  const employee = useAppSelector(
    s => (s as any).auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const isAdmin = isEmployeeAdminRole(employee?.bp_employee_role);
  const searchTerm = useAppSelector(
    s => (s as any).backofficeOrders?.tableSearchTerm ?? '',
  );
  const {
    setRange,
    startISO,
    endISO,
    setDateFilterActive,
  } = useBackOfficeOrdersFilter();
  const [showFilter, setShowFilter] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const debounceRef = useRef<any>(null);

  const searchHandlers = {
    onChangeText: (text: string) => {
      dispatch(setTableSearchTerm(text));
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const trimmed = String(text || '').trim();
        if (!trimmed) {
          dispatch(clearTableSearch());
          return;
        }
        void triggerSearch(1, trimmed);
      }, 350);
    },
    onSubmit: () => {
      const trimmed = String(searchTerm || '').trim();
      if (!trimmed) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      void triggerSearch(1, trimmed);
    },
    onClear: () => {
      dispatch(setTableSearchTerm(''));
      dispatch(clearTableSearch());
    },
  };

  const searchBar = (
    <View
      style={{
        width: '100%',
        maxWidth: nativeHeaderSearchWidth,
      }}>
      <AppSearchBar
        placeholder="Customer name or Receipt id..."
        value={searchTerm}
        onChangeText={searchHandlers.onChangeText}
        onSubmit={searchHandlers.onSubmit}
        onClear={searchHandlers.onClear}
        width={nativeHeaderSearchWidth}
        height={v(50)}
        showLeadingIcon={true}
        showBarcodeScan={false}
        containerStyle={{
          backgroundColor: '#fff',
          borderColor: '#fff',
          borderWidth: 0,
        }}
      />
    </View>
  );

  const webSearchBar = (
    <View
      style={{
        width: '100%',
        maxWidth: webHeaderSearchMaxWidth,
      }}>
      <AppSearchBarWeb
        placeholder="Customer name or Receipt id..."
        value={searchTerm}
        onChangeText={searchHandlers.onChangeText}
        onSubmit={searchHandlers.onSubmit}
        onClear={searchHandlers.onClear}
        width={
          showTabletSidebarLayoutSetting && useSidebar
            ? width * 0.46
            : width * 0.32
        }
        height={v(50)}
        showLeadingIcon={true}
        showBarcodeScan={false}
        containerStyle={{
          backgroundColor: '#fff',
          borderColor: '#fff',
          borderWidth: 0,
        }}
      />
    </View>
  );

  const handleRefreshOrders = useCallback(() => {
    setRefreshVersion(v => v + 1);
  }, []);

  const handleOpenOrderSettings = useCallback(() => {
    try {
      (navigation as any).navigate('OrderSettingsScreen');
    } catch (e) {
      (navigation as any).getParent?.()?.navigate('OrderSettingsScreen');
    }
  }, [navigation]);

  const headerActions = (
    <HeaderActions
      actions={[
        {
          type: 'icon',
          icon: 'calendar-month-outline',
          onPress: () => setShowFilter(prev => !prev),
          accessibilityLabel: 'Filter by date',
        },
        {
          type: 'menu',
          items: [
            {
              title: 'Refresh orders',
              leadingIcon: 'reload',
              onPress: handleRefreshOrders,
            },
            {
              title: 'Order Settings',
              leadingIcon: 'cog-outline',
              onPress: handleOpenOrderSettings,
              visible: isAdmin,
            },
          ],
        },
      ]}
    />
  );

  // Non-sidebar: inject into parent navigator header via setOptions
  useLayoutEffect(() => {
    if (useSidebar) return;
    navigation.setOptions({
      headerTitleContainerStyle: { flex: 1 },
      headerTitle: () => searchBar,
      headerRight: () => headerActions,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, searchTerm, dispatch, width, showFilter, useSidebar]);

  // Sidebar: inject WebHeaderBar via WebShellContext
  const webHeaderNode = useMemo(() => {
    if (!useSidebar) return null;
    return (
      <WebHeaderBar
        onBack={() => {
          if (navigation.canGoBack()) {
            navigation.goBack();
            return;
          }

          (navigation as any).navigate('BackOffice', {
            screen: 'Dashboard',
          });
        }}
        title={
          showTabletSidebarLayoutSetting && useSidebar
            ? undefined
            : <BackOfficeHeaderLocationPicker />
        }
        titleAlign={
          showTabletSidebarLayoutSetting && useSidebar ? undefined : 'center'
        }
        rightSlot={headerActions}>
        {webSearchBar}
      </WebHeaderBar>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navigation,
    searchTerm,
    showFilter,
    showTabletSidebarLayoutSetting,
    useSidebar,
    width,
  ]);

  useWebHeader(webHeaderNode, Boolean(webHeaderNode));

  const normalizePaginated = useCallback(
    (payload: any, page: number) => ({
      docs: Array.isArray(payload?.docs)
        ? payload.docs
        : Array.isArray(payload?.data)
          ? payload.data
          : [],
      hasNextPage: Boolean(
        payload?.hasNextPage ??
        (payload?.page ?? page) < (payload?.totalPages ?? 0),
      ),
      hasPrevPage: Boolean(payload?.hasPrevPage ?? (payload?.page ?? page) > 1),
      limit: Number(payload?.limit ?? payload?.perPage ?? 100),
      nextPage: Number(
        payload?.nextPage ??
        ((payload?.page ?? page) < (payload?.totalPages ?? 0)
          ? (payload?.page ?? page) + 1
          : (payload?.page ?? page)),
      ),
      page: Number(payload?.page ?? page),
      perPage: Number(payload?.perPage ?? payload?.limit ?? 100),
      totalDocs: Number(payload?.totalDocs ?? payload?.total ?? 0),
      totalPages: Number(
        payload?.totalPages ??
        Math.max(
          1,
          Math.ceil(
            Number(payload?.totalDocs ?? payload?.total ?? 0) /
            Number(payload?.perPage ?? payload?.limit ?? 100),
          ),
        ),
      ),
    }),
    [],
  );

  const triggerSearch = useCallback(
    async (page: number, term: string) => {
      if (!owner?.accessToken || !ownerId || !term) return;
      try {
        dispatch(setOrdersLoading(true));
        dispatch(setOrdersError(null));
        const res = await searchOrderByReceiptId(
          axiosBackoffice as any,
          ownerId,
          page,
          term,
          { accessToken: owner?.accessToken },
        );
        if (res?.status === 200) {
          const payloadRaw = (res as any)?.data?.data ?? (res as any)?.data;
          const normalized = normalizePaginated(payloadRaw, page);
          dispatch(setTableSearchResults(normalized as any));
          dispatch(
            setTableSearchPagination({
              page: normalized.page,
              perPage: normalized.perPage,
            }),
          );
        }
      } catch (e: any) {
        dispatch(setOrdersError(e?.message || 'Failed to search orders'));
      } finally {
        dispatch(setOrdersLoading(false));
      }
    },
    [
      owner?.accessToken,
      ownerId,
      axiosBackoffice,
      dispatch,
      normalizePaginated,
    ],
  );

  const tabConfiguration = useMemo(() => {
    const baseScreenOptions = {
      tabBarLabelStyle: {
        fontSize: fs(15),
        lineHeight: fs(18),
        fontFamily: GTFonts.regular,
      },
      tabBarIndicatorStyle: { backgroundColor: theme.colors.primary },
      tabBarActiveTintColor: theme.colors.primary,
      tabBarBounces: true,
      tabBarPressOpacity: 1,
      lazy: true,
      lazyPreloadDistance: 1,
      initialLayout: { width: Dimensions.get('window').width },
      swipeVelocityImpact: 0.2,
      springConfig: {
        damping: 20,
        stiffness: 100,
        restSpeedThreshold: 0.001,
        restDisplacementThreshold: 0.001,
      },
    } as const;
    return {
      screenOptions: baseScreenOptions,
      showPartialOrders: settings?.allow_partial_orders !== false,
    };
  }, [settings?.allow_partial_orders]);

  const hideCompleted = Boolean((route?.params as any)?.hideCompleted);
  const presetRaw = String((route?.params as any)?.preset || '').toLowerCase();
  const allUnpaid = Boolean((route?.params as any)?.allUnpaid);

  // The "Still Owed" deep link sets allUnpaid=true and drops date bounds.
  // When the screen gains focus, apply the param; when it loses focus (tab
  // switch, back navigation, etc.), reset everything so a later normal visit
  // doesn't inherit stale params. useFocusEffect cleanup fires on blur —
  // unlike useEffect cleanup which only fires on unmount (never on bottom tabs).
  useFocusEffect(
    useCallback(() => {
      setDateFilterActive(!allUnpaid);
      return () => {
        // If we're pushing into order details (stack push, not tab switch),
        // keep the deep-link params so Still Owed mode survives the round trip.
        const parent = (navigation as any).getParent?.();
        const state = parent?.getState?.();
        const focused = state?.routes?.[state.index]?.name;
        if (focused === 'BackOfficeOrderDetails') return;

        setDateFilterActive(true);
        (navigation as any).setParams({ preset: undefined, hideCompleted: undefined, allUnpaid: undefined });
      };
    }, [allUnpaid, setDateFilterActive, navigation]),
  );

  const initialRouteName = useMemo(() => {
    if (presetRaw.includes('partial') && tabConfiguration.showPartialOrders)
      return 'Partially Paid';
    if (presetRaw.includes('pending') || hideCompleted) return 'Pending';
    return 'Completed';
  }, [presetRaw, hideCompleted, tabConfiguration.showPartialOrders]);

  return (
    <View style={{ flex: 1 }}>
      {showFilter ? (
        <View style={{ paddingHorizontal: s(5), marginTop: v(10) }}>
          <Filter
            title="Filter Orders By Date"
            mode="daily"
            initialStartISO={startISO}
            initialEndISO={endISO}
            onApply={(from, to) => {
              setRange(from, to);
            }}
          />
        </View>
      ) : null}
      <Tab.Navigator
        initialRouteName={initialRouteName}
        screenOptions={tabConfiguration.screenOptions}>
        {hideCompleted ? null : (
          <Tab.Screen
            name="Completed"
            options={{ title: 'Completed', animationEnabled: true }}>
            {props => (
              <BackOfficeCompletedOrders
                {...props}
                refreshVersion={refreshVersion}
              />
            )}
          </Tab.Screen>
        )}
        <Tab.Screen
          name="Pending"
          options={{ title: 'Pending', animationEnabled: true }}>
          {props => (
            <BackOfficePendingOrders
              {...props}
              refreshVersion={refreshVersion}
            />
          )}
        </Tab.Screen>
        {tabConfiguration.showPartialOrders ? (
          <Tab.Screen
            name="Partially Paid"
            options={{ title: 'Partially Paid', animationEnabled: true }}>
            {props => (
              <BackOfficePartialOrders
                {...props}
                refreshVersion={refreshVersion}
              />
            )}
          </Tab.Screen>
        ) : null}
      </Tab.Navigator>
    </View>
  );
};

const BackOfficeOrdersTabNavigator: React.FC = () => {
  return (
    <BackOfficeOrdersFilterProvider>
      <InnerTabs />
    </BackOfficeOrdersFilterProvider>
  );
};

export default BackOfficeOrdersTabNavigator;
